import type { ScheduledPlanChangeDto } from '@up2stay/up2promo-api-client';
import { type WorkspacePlan } from '@up2stay/up2promo-api-client';
import { Alert, AlertDescription } from '@up2stay/up2promo-ui';
import { Button } from '@up2stay/up2promo-ui';
import { CalendarClock } from 'lucide-react';

import { useEntitlementLabels } from '@/core/components/billing/upgrade-surfaces';
import { useCancelScheduledPlanChange } from '@/core/hooks/use-billing';
import { useDictionary } from '@/core/providers/dictionary-provider';

function formatDate(value: string | null | undefined, locale: string): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

export function PendingPlanChangeBanner({
  scheduledChange,
}: {
  scheduledChange: ScheduledPlanChangeDto | null | undefined;
}) {
  const dict = useDictionary();
  const billingDict = dict.workspace.billing;
  const { getPlanLabel } = useEntitlementLabels();
  const cancelScheduled = useCancelScheduledPlanChange();
  const locale =
    typeof navigator !== 'undefined' ? navigator.language : 'de-DE';

  if (!scheduledChange?.changeType) {
    return null;
  }

  const targetLabel = scheduledChange.plan
    ? getPlanLabel(scheduledChange.plan as WorkspacePlan)
    : billingDict.scheduledChange.cancelTarget;
  const effectiveDate = formatDate(scheduledChange.effectiveAt, locale);

  const message =
    scheduledChange.changeType === 'cancel'
      ? billingDict.scheduledChange.cancelMessage.replace(
          '{date}',
          effectiveDate,
        )
      : billingDict.scheduledChange.downgradeMessage
          .replace('{plan}', targetLabel)
          .replace('{date}', effectiveDate);

  return (
    <Alert>
      <CalendarClock className="h-4 w-4" />
      <AlertDescription className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <span>{message}</span>
        <Button
          size="sm"
          variant="outline"
          disabled={cancelScheduled.isPending}
          onClick={() => cancelScheduled.mutate(undefined)}
        >
          {billingDict.scheduledChange.keepCurrentPlan}
        </Button>
      </AlertDescription>
    </Alert>
  );
}
